import { useDateAvailCheck } from "kz-ui-sdk";
import moment from "moment";
import { ReactNode, useCallback, useEffect, useState } from "react";

const THREE_DAYS = 24 * 60 * 60 * 3;
const EXTERNAL_INSTALLER_REMINDER_KEY = "external-installer-reminder";

interface ExternalInstallerProps {
  reminderPeriod?: number; // in seconds
  reminderKey?: string;
  onDismissed?: () => void;
  onInstallable?: (state: boolean) => void;
  children: (handleInstall: () => void, handleClose: () => void) => ReactNode;
  /**
   * If true, skip the dismissed check
   */
  ignoreDismissed?: boolean;
  /**
   * If true, skip the self check
   * The parent component should handle the check
   */
  ignoreSelfCheck?: boolean;
  externalUrl?: string;
}

const ExternalInstaller = ({
  children,
  reminderPeriod = THREE_DAYS,
  reminderKey = EXTERNAL_INSTALLER_REMINDER_KEY,
  onDismissed,
  onInstallable,
  ignoreDismissed = false,
  ignoreSelfCheck = false,
  externalUrl,
}: ExternalInstallerProps) => {
  const [installable, setInstallable] = useState(false);

  const { dueAt: nextDisplayAt, updateDueAt: setNextDisplayAt } = useDateAvailCheck(reminderKey);

  useEffect(() => {
    onInstallable?.(installable);
  }, [installable, onInstallable]);

  useEffect(() => {
    const evaluateInstallability = () => {
      let shouldBeInstallable = false;

      if (ignoreSelfCheck) {
        shouldBeInstallable = true;
      } else if (externalUrl) {
        // Check if the user has dismissed the banner
        const isDisplaying = nextDisplayAt ? moment().isAfter(nextDisplayAt) : true;
        shouldBeInstallable = isDisplaying || ignoreDismissed;
      }
      // If no externalUrl, shouldBeInstallable remains false

      setInstallable(shouldBeInstallable);
    };

    // Delay evaluation by 10ms to prevent flicker
    const timeoutId = setTimeout(evaluateInstallability, 10);

    return () => clearTimeout(timeoutId);
  }, [nextDisplayAt, ignoreDismissed, ignoreSelfCheck, externalUrl]);

  const handleClose = useCallback(() => {
    setNextDisplayAt(moment().add(reminderPeriod, "seconds"));
    setInstallable(false);
    onDismissed?.();
  }, [reminderPeriod, setNextDisplayAt, onDismissed]);

  const handleInstall = useCallback(() => {
    if (externalUrl) {
      window.open(externalUrl, "_blank", "noopener,noreferrer");
    }
  }, [externalUrl]);

  return <>{installable && children(handleInstall, handleClose)}</>;
};

export default ExternalInstaller;
